import { useState, useEffect } from "react";
import {
  Button,
  Table,
  Form,
  Card,
  Dropdown,
  Offcanvas,
  Row,
  Col,
} from "react-bootstrap";
import DashboardLayout from "../components/layout/DashboardLayout";
import ReactPaginate from "react-paginate";
import { toast } from "react-toastify";
import { PropagateLoader } from "react-spinners";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslate } from "../hooks/LanguageProvider";
import DocumentPreviewModal, {
  PreviewableDocument,
} from "../components/common/DocumentPreviewModal";
import { buildPreviewableDocument } from "../utilities/uploadFiles";

import {
  FaCertificate,
  FaPlus,
  FaEdit,
  FaTrash,
  FaSearch,
  FaDownload,
  FaEye,
  FaArrowLeft,
} from "react-icons/fa";

import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const backendUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:5002";
interface Certification {
  id: number;
  id_certification: number;
  certificate_number: string;
  certification_name: string;
  issued_date: string;
  expiry_date: string;
  certificate_file?: string;
  original_file_name?: string;
  is_required?: number;
  linked_product?: string;
  linked_constructor?: string;
  conformity_status?: string;
  export_impact?: string;
}

export default function OEMCertifications() {
  const { translate } = useTranslate();
  const t = translate;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const id_user =
    searchParams.get("id_user") ?? localStorage.getItem("userid") ?? "";

  const [certifications, setCertifications] = useState<Certification[]>([]);
  const [search, setSearch] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [previewFile, setPreviewFile] = useState<PreviewableDocument | null>(
    null,
  );

  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);

  const [errors, setErrors] = useState({
    id_certification: "",
  });
  const [certificationTypes, setCertificationTypes] = useState<any[]>([]);
  const [form, setForm] = useState({
    id_certification: "",
    certificate_number: "",
    issued_date: "",
    expiry_date: "",
    certificate_file: "",
    original_file_name: "",
    is_required: "0",
    linked_product: "",
    linked_constructor: "",
    conformity_status: "",
    export_impact: "",
  });

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isFileDragging, setIsFileDragging] = useState(false);
  const [loading, setLoading] = useState(false);

  const [colum, setSortColum] = useState("certification_name");
  const [searchField, setSearchField] = useState("certificate_number");
  const [sort, setSort] = useState("ASC");

  const [selectedColumns, setSelectedColumns] = useState({
    certification_name: true,
    certificate_number: true,
    issued_date: true,
    expiry_date: true,
    is_required: true,
    linked_product: true,
    linked_constructor: true,
    conformity_status: true,
    export_impact: true,
  });

  const [selectedRows, setSelectedRows] = useState<number[]>([]);

  function goBackToOEMProfile() {
    if (id_user) {
      navigate(`/oem-profile/${id_user}`);
    } else {
      navigate("/oem-profile");
    }
  }

  const displayValue = (value: any) =>
    value === null || value === undefined || value === ""
      ? "Non renseignÃ©"
      : value;

  const formatYesNo = (value: any) => (Number(value) === 1 ? "Oui" : "Non");

  /* ================= DATE FORMAT ================= */

  function formatDate(date: string) {
    if (!date) return "";

    const [year, month, day] = String(date).split("-");
    if (!year || !month || !day) return date;

    return `${day}/${month}/${year}`;
  }

  function formatDateForInput(date: string) {
    return date || "";
  }
  /* ================= LOAD ================= */

  async function loadCertifications() {
    try {
      const res = await fetch(
        `${backendUrl}/api/oems/${id_user}/certifications`,
      );
      const data = await res.json();

      setCertifications(data);
    } catch {
      toast.error(t("error_loading_certifications"));
    }
  }
  async function loadCertificationTypes() {
    try {
      const res = await fetch(`${backendUrl}/api/certification-types`);
      const data = await res.json();

      if (Array.isArray(data)) {
        setCertificationTypes(data);
      } else {
        console.error("Certification types is not array:", data);
        setCertificationTypes([]);
      }
    } catch (err) {
      console.error(err);
      setCertificationTypes([]);
    }
  }
  useEffect(() => {
    loadCertifications();
    loadCertificationTypes();
  }, []);

  /* ================= SEARCH ================= */
  const filtered = certifications.filter((c) => {
    const value = (c as any)[searchField]?.toString().toLowerCase() || "";
    return value.includes(search.toLowerCase());
  });
  /* ================= PAGINATION ================= */

  const pageCount = Math.ceil(filtered.length / limit);
  const offset = page * limit;
  const current = filtered.slice(offset, offset + limit);

  /* ================= SORT================= */

  const handleSortingColum = async (column: string) => {
    if (loading) return;

    const newSort = sort === "ASC" ? "DESC" : "ASC";

    setSortColum(column);
    setSort(newSort);
    setLoading(true);

    try {
      const res = await fetch(
        `${backendUrl}/api/oems/${id_user}/certifications`,
      );
      const data = await res.json();

      const sorted = [...data].sort((a: any, b: any) => {
        if (newSort === "ASC") return a[column] > b[column] ? 1 : -1;
        return a[column] < b[column] ? 1 : -1;
      });

      setCertifications(sorted);
    } catch {
      toast.error(t("error_sorting"));
    } finally {
      setLoading(false);
    }
  };

  /* ================= EXPORT ================= */

  function exportExcel() {
    const ws = XLSX.utils.json_to_sheet(certifications);
    const wb = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(wb, ws, "Certifications");

    XLSX.writeFile(wb, "certifications.xlsx");
  }

  function exportCSV() {
    const ws = XLSX.utils.json_to_sheet(certifications);
    const csv = XLSX.utils.sheet_to_csv(ws);

    const blob = new Blob([csv]);
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = "certifications.csv";
    a.click();
  }

  function exportPDF() {
    const doc = new jsPDF();

    autoTable(doc, {
      head: [["Certification", "Certificate Number", "Issued", "Expiry"]],
      body: certifications.map((c) => [
        c.id_certification,
        c.certificate_number,
        formatDate(c.issued_date),
        formatDate(c.expiry_date),
      ]),
    });

    doc.save("certifications.pdf");
  }

  /* ================= VALIDATE NUMBER LIVE ================= */

  function handleCertificationId(value: string) {
    if (value === "" || /^[0-9]+$/.test(value)) {
      setErrors({ ...errors, id_certification: "" });
    } else {
      setErrors({ ...errors, id_certification: t("must_be_number") });
    }

    setForm({ ...form, id_certification: value });
  }

  /* ================= RESET ================= */

  function resetForm() {
    setEditId(null);
    setSelectedFile(null);
    setIsFileDragging(false);
    setForm({
      id_certification: "",
      certificate_number: "",
      issued_date: "",
      expiry_date: "",
      certificate_file: "",
      original_file_name: "",
      is_required: "0",
      linked_product: "",
      linked_constructor: "",
      conformity_status: "",
      export_impact: "",
    });

    setErrors({
      id_certification: "",
    });

    setShowModal(false);
  }

  const openCertificateFilePicker = () => {
    document.getElementById("certificateFileInput")?.click();
  };

  const setCertificateUploadFile = (file?: File | null) => {
    if (!file) return;

    const isAcceptedFile = /\.(pdf|jpg|jpeg|png)$/i.test(file.name);
    if (!isAcceptedFile) {
      toast.error(t("accepted_file_formats"));
      return;
    }

    setSelectedFile(file);
  };

  const handleCertificateDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsFileDragging(false);
    setCertificateUploadFile(e.dataTransfer.files?.[0]);
  };

  function validateCertificationForm() {
    const newErrors = {
      id_certification: "",
    };

    let isValid = true;

    if (!form.id_certification) {
      newErrors.id_certification = t("certification_required");
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  }
  /* ================= ADD ================= */

  async function addCertification() {
    if (!validateCertificationForm()) return;

    try {
      const formData = new FormData();

      formData.append("id_user", String(id_user));
      formData.append("id_certification", form.id_certification);
      formData.append("certificate_number", form.certificate_number);
      formData.append("issued_date", form.issued_date);
      formData.append("expiry_date", form.expiry_date);
      formData.append("is_required", form.is_required);
      formData.append("linked_product", form.linked_product);
      formData.append("linked_constructor", form.linked_constructor);
      formData.append("conformity_status", form.conformity_status);
      formData.append("export_impact", form.export_impact);

      if (selectedFile) {
        formData.append("certificate_file", selectedFile);
      }

      const res = await fetch(
        `${backendUrl}/api/oems/certifications/create`,
        {
          method: "POST",
          body: formData,
        },
      );

      if (!res.ok) throw new Error();

      toast.success(t("certification_added"));

      resetForm();
      setSelectedFile(null);
      loadCertifications();
    } catch (error) {
      console.error(error);
      toast.error(t("error_adding_certification"));
    }
  }

  /* ================= UPDATE ================= */

  async function updateCertification() {
    if (!editId) return;
    if (!validateCertificationForm()) return;

    try {
      const formData = new FormData();

      formData.append("id_certification", form.id_certification);
      formData.append("certificate_number", form.certificate_number);
      formData.append("issued_date", form.issued_date);
      formData.append("expiry_date", form.expiry_date);

      if (selectedFile) {
        formData.append("certificate_file", selectedFile);
      } else {
        formData.append("existing_file", form.certificate_file);
        formData.append("original_file_name", form.original_file_name);
      }

      const res = await fetch(
        `${backendUrl}/api/oems/certifications/update/${editId}`,
        {
          method: "PUT",
          body: formData,
        },
      );

      if (!res.ok) throw new Error();

      toast.success(t("certification_updated"));

      resetForm();
      setSelectedFile(null);
      loadCertifications();
    } catch (error) {
      console.error(error);
      toast.error(t("error_updating_certification"));
    }
  }

  /* ================= DELETE ================= */

  async function deleteCertification(id: number) {
    if (!window.confirm(t("delete_certification_confirm"))) return;

    await fetch(`${backendUrl}/api/oems/certifications/${id}`, {
      method: "DELETE",
    });

    toast.success(t("certification_deleted"));

    loadCertifications();
  }
  const handleSelectAll = () => {
    if (selectedRows.length === current.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(current.map((c) => c.id));
    }
  };

  const handleSelectRow = (id: number) => {
    if (selectedRows.includes(id)) {
      setSelectedRows(selectedRows.filter((i) => i !== id));
    } else {
      setSelectedRows([...selectedRows, id]);
    }
  };

  const handleColumnChange = (column: string) => {
    setSelectedColumns((prev: any) => ({
      ...prev,
      [column]: !prev[column],
    }));
  };

  const openCertificatePreview = (certification: Certification) => {
    const preview = buildPreviewableDocument(
      certification.certificate_file,
      certification.original_file_name || certification.certificate_number,
      "certifications",
      t("certificate_file"),
    );

    if (preview) setPreviewFile(preview);
  };

  /* ================= UI ================= */

  return (
    <div className="container-fluid" style={{ minHeight: "auto" }}>
      <div className="container-fluid">
        {/* HEADER */}

        <div className="d-flex justify-content-between mb-4">
          <h4>
            <FaCertificate style={{ marginRight: 10 }} />
            {t("certifications")} ({certifications.length})
          </h4>

          <div>
            <Button
              variant="outline-secondary"
              className="me-2"
              onClick={goBackToOEMProfile}
            >
              <FaArrowLeft className="me-1" /> {t("back_to_oem_profile") !== "back_to_oem_profile" ? t("back_to_oem_profile") : "Retour fiche OEM"}
            </Button>

            <Dropdown className="d-inline me-2">
              <Dropdown.Toggle>
                <FaDownload /> {t("export")}
              </Dropdown.Toggle>

              <Dropdown.Menu>
                <Dropdown.Item onClick={exportExcel}>Excel</Dropdown.Item>

                <Dropdown.Item onClick={exportCSV}>CSV</Dropdown.Item>

                <Dropdown.Item onClick={exportPDF}>PDF</Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>

            <Button onClick={() => setShowModal(true)}>
              <FaPlus /> {t("add_certification")}
            </Button>
          </div>
        </div>
      </div>

      {/* SEARCH */}
      <div className="row mb-3">
        <div className="col-md-4">
          <div className="input-group">
            <Dropdown>
              <Dropdown.Toggle variant="link">
                <i className="fas fa-chevron-down"></i>
              </Dropdown.Toggle>

              <Dropdown.Menu>
                <Dropdown.Item
                  onClick={() => setSearchField("certificate_number")}
                >
                  {t("number")}
                </Dropdown.Item>
                <Dropdown.Item
                  onClick={() => setSearchField("certification_name")}
                >
                  {t("certification")}
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>

            <input
              type="text"
              className="form-control"
              placeholder={`${t("search_by")} ${t(searchField)}`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />

            <Button variant="secondary" onClick={() => setSearch("")}>
              âœ•
            </Button>
          </div>
        </div>

        <div className="col-md-8 d-flex justify-content-end align-items-center">
          <div className="dataTables_length d-flex align-items-center">
            <label style={{ marginBottom: 0 }}>
              {t("show")}
              <select
                className="custom-select custom-select-sm form-control form-control-sm ms-2"
                style={{
                  width: "70px",
                  height: "30px",
                  padding: "2px 6px",
                  fontSize: "12px",
                }}
                value={limit}
                onChange={(e) => {
                  setLimit(Number(e.target.value));
                  setPage(0);
                }}
              >
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
                <option value="100">100</option>
                <option value="200">200</option>
                <option value="500">500</option>
              </select>
            </label>
          </div>

          <Dropdown>
            <Dropdown.Toggle variant="">ðŸ‘</Dropdown.Toggle>

            <Dropdown.Menu>
              {Object.keys(selectedColumns).map((col) => (
                <Dropdown.Item key={col}>
                  <input
                    type="checkbox"
                    checked={
                      selectedColumns[col as keyof typeof selectedColumns]
                    }
                    onChange={() => handleColumnChange(col)}
                  />{" "}
                  {t(col)}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>

      {/* TABLE */}

      <div className="table-responsive">
        <Table className="dataTable">
          <thead>
            <tr className="ligth ligth-data">
              <th style={{ width: "5%" }}>
                <div className="checkbox d-inline-block">
                  <input
                    type="checkbox"
                    className="checkbox-input"
                    checked={selectedRows.length === current.length}
                    onChange={handleSelectAll}
                  />
                </div>
              </th>

              {selectedColumns.certification_name && (
                <th
                  className="sorting"
                  onClick={() => handleSortingColum("certification_name")}
                >
                  {t("certification")}
                </th>
              )}
              {selectedColumns.certificate_number && (
                <th
                  className="sorting"
                  onClick={() => handleSortingColum("certificate_number")}
                >
                  {t("certificate_number")}
                </th>
              )}

              {selectedColumns.issued_date && (
                <th
                  className="sorting"
                  onClick={() => handleSortingColum("issued_date")}
                >
                  {t("issued")}
                </th>
              )}

              {selectedColumns.expiry_date && (
                <th
                  className="sorting"
                  onClick={() => handleSortingColum("expiry_date")}
                >
                  {t("expiry")}
                </th>
              )}
              {selectedColumns.is_required && <th>Obligatoire</th>}
              {selectedColumns.linked_product && <th>Produit liÃ©</th>}
              {selectedColumns.linked_constructor && <th>Constructeur liÃ©</th>}
              {selectedColumns.conformity_status && <th>Statut conformitÃ©</th>}
              {selectedColumns.export_impact && <th>Impact export</th>}
              <th>{t("file")}</th>
              <th className="text-center">{t("actions")}</th>
            </tr>
          </thead>

          <tbody className="ligth-body">
            {loading ? (
              <tr>
                <td colSpan={12} className="text-center">
                  <PropagateLoader color="#123abc" />
                </td>
              </tr>
            ) : current.length > 0 ? (
              current.map((c) => (
                <tr key={c.id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedRows.includes(c.id)}
                      onChange={() => handleSelectRow(c.id)}
                    />
                  </td>

                  {selectedColumns.certification_name && (
                    <td>{c.certification_name}</td>
                  )}

                  {selectedColumns.certificate_number && (
                    <td>{c.certificate_number}</td>
                  )}

                  {selectedColumns.issued_date && (
                    <td>{formatDate(c.issued_date)}</td>
                  )}

                  {selectedColumns.expiry_date && (
                    <td>{formatDate(c.expiry_date)}</td>
                  )}
                  {selectedColumns.is_required && (
                    <td>{formatYesNo(c.is_required)}</td>
                  )}
                  {selectedColumns.linked_product && (
                    <td>{displayValue(c.linked_product)}</td>
                  )}
                  {selectedColumns.linked_constructor && (
                    <td>{displayValue(c.linked_constructor)}</td>
                  )}
                  {selectedColumns.conformity_status && (
                    <td>{displayValue(c.conformity_status)}</td>
                  )}
                  {selectedColumns.export_impact && (
                    <td>{displayValue(c.export_impact)}</td>
                  )}

                  <td>
                    {c.certificate_file ? (
                      <button
                        type="button"
                        onClick={() => openCertificatePreview(c)}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "6px",
                          padding: "6px 16px",
                          borderRadius: "999px",
                          fontSize: "13px",
                          fontWeight: 600,
                          textDecoration: "none",
                          background: "#fff7ed",
                          color: "#d97706",
                          border: "1px solid #fbbf24",
                          transition: "all 0.2s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = "#ffedd5";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "#fff7ed";
                        }}
                      >
                        <FaEye size={12} />
                        {t("view")}
                      </button>
                    ) : (
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          padding: "6px 16px",
                          borderRadius: "999px",
                          fontSize: "13px",
                          fontWeight: 600,
                          background: "#f9fafb",
                          color: "#6b7280",
                          border: "1px solid #e5e7eb",
                        }}
                      >
                        {t("no_file")}
                      </span>
                    )}
                  </td>

                  <td className="text-center">
                    <div className="d-flex justify-content-center align-items-center gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setEditId(c.id);
                          setForm({
                            id_certification: String(c.id_certification),
                            certificate_number: c.certificate_number,
                            issued_date: formatDateForInput(c.issued_date),
                            expiry_date: formatDateForInput(c.expiry_date),
                            certificate_file: c.certificate_file || "",
                            original_file_name: c.original_file_name || "",
                            is_required: String(c.is_required || 0),
                            linked_product: c.linked_product || "",
                            linked_constructor: c.linked_constructor || "",
                            conformity_status: c.conformity_status || "",
                            export_impact: c.export_impact || "",
                          });
                          setSelectedFile(null);
                          setShowModal(true);
                        }}
                        style={{
                          width: "38px",
                          height: "38px",
                          borderRadius: "50%",
                          border: "none",
                          background: "#f2c94c",
                          color: "#1f2937",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          boxShadow: "0 2px 6px rgba(0,0,0,0.10)",
                        }}
                        title={t("edit")}
                      >
                        <FaEdit size={15} />
                      </button>

                      <button
                        type="button"
                        onClick={() => deleteCertification(c.id)}
                        style={{
                          width: "38px",
                          height: "38px",
                          borderRadius: "50%",
                          border: "none",
                          background: "#dc3545",
                          color: "#ffffff",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
                        }}
                        title={t("delete")}
                      >
                        <FaTrash size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={12} style={{ textAlign: "center" }}>
                  {t("no_certifications")}
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
      {/* PAGINATION */}
      <div className="d-flex justify-content-between mt-3">
        <span>
          {t("displaying")} {current.length} {t("out_of")} {filtered.length}
        </span>

        <ReactPaginate
          previousLabel={t("previous")}
          nextLabel={t("next")}
          breakLabel={"..."}
          pageCount={pageCount}
          marginPagesDisplayed={2}
          pageRangeDisplayed={3}
          onPageChange={(d) => setPage(d.selected)}
          containerClassName={"pagination justify-content-end"}
          pageClassName={"page-item"}
          pageLinkClassName={"page-link"}
          previousClassName={"page-item"}
          previousLinkClassName={"page-link"}
          nextClassName={"page-item"}
          nextLinkClassName={"page-link"}
          activeClassName={"active"}
          forcePage={page}
        />
      </div>
      <Offcanvas
        show={showModal}
        onHide={resetForm}
        placement="end"
        className="certification-drawer"
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>
            {editId ? t("edit_certification") : t("add_certification")}
          </Offcanvas.Title>
        </Offcanvas.Header>

        <Offcanvas.Body>
          <Form className="certification-drawer-form">
            <div className="certification-section-title">
              <span className="certification-section-icon">
                <FaCertificate size={13} />
              </span>
              <span>{t("certification")}</span>
            </div>

            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>{t("certification_id")}</Form.Label>
                  <Form.Select
                    value={form.id_certification}
                    onChange={(e) =>
                      setForm({ ...form, id_certification: e.target.value })
                    }
                  >
                    <option value="">{t("select_certification")}</option>

                    {Array.isArray(certificationTypes) &&
                      certificationTypes.map((c) => (
                        <option
                          key={c.id_certification}
                          value={c.id_certification}
                        >
                          {c.name}
                        </option>
                      ))}
                  </Form.Select>

                  {errors.id_certification && (
                    <small className="text-danger">
                      {errors.id_certification}
                    </small>
                  )}
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label>{t("certificate_number")}</Form.Label>

                  <Form.Control
                    value={form.certificate_number}
                    onChange={(e) =>
                      setForm({ ...form, certificate_number: e.target.value })
                    }
                  />
                </Form.Group>
              </Col>
            </Row>

            <div className="certification-section-title mt-4">
              <span className="certification-section-icon">
                <i className="las la-calendar"></i>
              </span>
              <span>{t("validity")}</span>
            </div>

            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>{t("issued_date")}</Form.Label>

                  <Form.Control
                    type="date"
                    value={form.issued_date}
                    onChange={(e) =>
                      setForm({ ...form, issued_date: e.target.value })
                    }
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label>{t("expiry_date")}</Form.Label>

                  <Form.Control
                    type="date"
                    value={form.expiry_date}
                    onChange={(e) =>
                      setForm({ ...form, expiry_date: e.target.value })
                    }
                  />
                </Form.Group>
              </Col>
            </Row>

            <div className="certification-section-title">
              <span className="certification-section-icon">
                <i className="las la-clipboard-check"></i>
              </span>
              <span>{t("compliance")}</span>
            </div>

            <Row className="g-3">
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Certification obligatoire</Form.Label>
                  <Form.Select
                    value={form.is_required}
                    onChange={(e) =>
                      setForm({ ...form, is_required: e.target.value })
                    }
                  >
                    <option value="0">Non</option>
                    <option value="1">Oui</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <div className="certification-section-title">
              <span className="certification-section-icon">
                <i className="las la-link"></i>
              </span>
              <span>{t("links")}</span>
            </div>

            <Row className="g-3">
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Produit liÃ©</Form.Label>
                  <Form.Control
                    value={form.linked_product}
                    onChange={(e) =>
                      setForm({ ...form, linked_product: e.target.value })
                    }
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Constructeur liÃ©</Form.Label>
                  <Form.Control
                    value={form.linked_constructor}
                    placeholder="FIAT Ã  titre dâ€™exemple"
                    onChange={(e) =>
                      setForm({ ...form, linked_constructor: e.target.value })
                    }
                  />
                </Form.Group>
              </Col>
            </Row>

            <div className="certification-section-title mt-4">
              <span className="certification-section-icon">
                <i className="las la-clipboard-list"></i>
              </span>
              <span>{t("status")}</span>
            </div>

            <Row className="g-3">
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Statut conformitÃ©</Form.Label>
                  <Form.Select
                    value={form.conformity_status}
                    onChange={(e) =>
                      setForm({ ...form, conformity_status: e.target.value })
                    }
                  >
                    <option value="">Non renseignÃ©</option>
                    <option value="Conforme">Conforme</option>
                    <option value="Ã€ renouveler">Ã€ renouveler</option>
                    <option value="Manquante">Manquante</option>
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Impact export</Form.Label>
                  <Form.Select
                    value={form.export_impact}
                    onChange={(e) =>
                      setForm({ ...form, export_impact: e.target.value })
                    }
                  >
                    <option value="">Non renseignÃ©</option>
                    <option value="Bloquant">Bloquant</option>
                    <option value="Non bloquant">Non bloquant</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <div className="certification-section-title mt-4">
              <span className="certification-section-icon">
                <i className="las la-paperclip"></i>
              </span>
              <span>{t("certificate_file")}</span>
            </div>

            <Form.Group className="mb-0">
              <Form.Label>{t("certificate_file")}</Form.Label>

              <input
                id="certificateFileInput"
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                style={{ display: "none" }}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setCertificateUploadFile(e.target.files?.[0]);
                }}
              />

              <div
                className={`certification-upload-dropzone${
                  isFileDragging ? " is-dragging" : ""
                }${selectedFile ? " has-file" : ""}`}
                role="button"
                tabIndex={0}
                onClick={openCertificateFilePicker}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    openCertificateFilePicker();
                  }
                }}
                onDragEnter={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsFileDragging(true);
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsFileDragging(true);
                }}
                onDragLeave={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                    setIsFileDragging(false);
                  }
                }}
                onDrop={handleCertificateDrop}
              >
                <div className="certification-upload-icon">
                  <i className="las la-cloud-upload-alt"></i>
                </div>

                <div className="certification-upload-content">
                  <div className="certification-upload-action">
                    <span>{t("choose_file")}</span>
                    <small>Glisser-dÃ©poser</small>
                  </div>

                  <div className="certification-upload-name">
                    {selectedFile
                      ? selectedFile.name
                      : editId && form.original_file_name
                        ? form.original_file_name
                        : t("no_file_selected")}
                  </div>
                </div>
              </div>

              <div className="certification-upload-meta">
                <small className="text-muted">
                  {t("accepted_file_formats")}
                </small>

                {editId && form.original_file_name && !selectedFile && (
                  <small className="text-muted">
                    {t("current_file")}: {form.original_file_name}
                  </small>
                )}

                {selectedFile && (
                  <small className="text-success">
                    {t("new_file_selected")}: {selectedFile.name}
                  </small>
                )}
              </div>
            </Form.Group>
          </Form>
        </Offcanvas.Body>

        <div className="certification-drawer-footer">
          <Button variant="secondary" onClick={resetForm}>
            {t("cancel")}
          </Button>

          <Button
            variant="primary"
            onClick={editId ? updateCertification : addCertification}
          >
            {t("save")}
          </Button>
        </div>
      </Offcanvas>

      <DocumentPreviewModal
        document={previewFile}
        fallbackTitle={t("certificate_file")}
        onHide={() => setPreviewFile(null)}
        t={t}
      />
    </div>
  );
}

